import { Navigate, useLocation, type RouteObject } from 'react-router';

import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { FitterOnlyRouteGuard } from '@/auth/FitterOnlyRouteGuard';
import { ContactVerificationRouteGuard } from '@/auth/ContactVerificationRouteGuard';
import { InsurerRouteGuard } from '@/auth/InsurerRouteGuard';
import { RestrictedRepairerRouteGuard } from '@/auth/RestrictedRepairerRouteGuard';
import { InventoryRouteGuard } from '@/auth/InventoryRouteGuard';
import { useAuth } from '@/auth/useAuth';
import { isInsurerUser } from '@/auth/roles';
import { canUseCourses } from '@/auth/trainingMode';
import { CLAIMFILTER_AUDIENCE_ROLES } from '@/auth/claimFilterAudience';
import { lazyScreen } from '@/lib/lazyScreen';

/*
 * Story fe-be-monteur-s5b-installeerbaar (EPIC-021, S021.5b) deel A — ELK scherm laadt pas
 * als zijn route opent (G-1). Alleen de guards hierboven zijn statisch: die beslissen vóór
 * er iets geladen wordt (een geweigerde gebruiker haalt het scherm dus nooit op).
 *
 * ⚠ Importeer hier nooit meer een scherm statisch. Het build-plugin
 *   (`buildtools/fitterChainPlugin.ts`) leidt de kantoorschermen af uit de `import()`'s in
 *   dit bestand en laat `pnpm build` falen zodra een ervan in de keten van `/monteur` zit.
 *
 * Volgorde = de oude importvolgorde, zodat een diff per scherm leesbaar blijft.
 */
const InsurerWorklistPage = lazyScreen(() => import('@/features/verzekeraar/InsurerWorklistPage'), 'InsurerWorklistPage');
const InsurerDossiersPage = lazyScreen(() => import('@/features/verzekeraar/InsurerDossiersPage'), 'InsurerDossiersPage');
const LoginRoute = lazyScreen(() => import('@/features/auth/LoginRoute'), 'LoginRoute');
const TokensPreviewPage = lazyScreen(() => import('@/features/_dev/TokensPreviewPage'), 'TokensPreviewPage');
const DashboardPage = lazyScreen(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage');
const DossiersPage = lazyScreen(() => import('@/features/dossiers/DossiersPage'), 'DossiersPage');
const DossierDetailPage = lazyScreen(() => import('@/features/dossiers/DossierDetailPage'), 'DossierDetailPage');
const IntakeDetailPage = lazyScreen(() => import('@/features/intakes/IntakeDetailPage'), 'IntakeDetailPage');
const MeldingenPage = lazyScreen(() => import('@/features/intakes/MeldingenPage'), 'MeldingenPage');
const CallCenterIntakePage = lazyScreen(
  () => import('@/features/intakes/callcenter/CallCenterIntakePage'),
  'CallCenterIntakePage',
);
const PlanbordPage = lazyScreen(() => import('@/features/planbord/PlanbordPage'), 'PlanbordPage');
const PlanbordSettingsPage = lazyScreen(() => import('@/features/planbord/PlanbordSettingsPage'), 'PlanbordSettingsPage');
const ItemTypesPage = lazyScreen(() => import('@/features/inrichting/itemtypes/ItemTypesPage'), 'ItemTypesPage');
const ItemTypeDetailPage = lazyScreen(() => import('@/features/inrichting/itemtypes/ItemTypeDetailPage'), 'ItemTypeDetailPage');
const ItemTypeEditPage = lazyScreen(() => import('@/features/inrichting/itemtypes/ItemTypeEditPage'), 'ItemTypeEditPage');
const ItemTypeDeletePage = lazyScreen(() => import('@/features/inrichting/itemtypes/ItemTypeDeletePage'), 'ItemTypeDeletePage');
const PriceListsPage = lazyScreen(() => import('@/features/inrichting/pricelists/PriceListsPage'), 'PriceListsPage');
const PriceListDetailPage = lazyScreen(() => import('@/features/inrichting/pricelists/PriceListDetailPage'), 'PriceListDetailPage');
const PriceListEditPage = lazyScreen(() => import('@/features/inrichting/pricelists/PriceListEditPage'), 'PriceListEditPage');
const PriceListDeletePage = lazyScreen(() => import('@/features/inrichting/pricelists/PriceListDeletePage'), 'PriceListDeletePage');
const PhotoProtocolListScreen = lazyScreen(() => import('@/features/inrichting/photoprotocols/PhotoProtocolListScreen'), 'PhotoProtocolListScreen');
const ControleredenenListPage = lazyScreen(() => import('@/features/inrichting/controleredenen/ControleredenenPage'), 'ControleredenenListPage');
const ControleredenenCreatePage = lazyScreen(() => import('@/features/inrichting/controleredenen/ControleredenenPage'), 'ControleredenenCreatePage');
const ControleredenenEditPage = lazyScreen(() => import('@/features/inrichting/controleredenen/ControleredenenPage'), 'ControleredenenEditPage');
const ControleredenenDeletePage = lazyScreen(() => import('@/features/inrichting/controleredenen/ControleredenenPage'), 'ControleredenenDeletePage');
const VerzendregelsPage = lazyScreen(() => import('@/features/inrichting/verzendregels/VerzendregelsPage'), 'VerzendregelsPage');
const DefaultDocumentsPage = lazyScreen(() => import('@/features/inrichting/defaultDocuments/DefaultDocumentsPage'), 'DefaultDocumentsPage');
const VervangendVervoerListPage = lazyScreen(() => import('@/features/inrichting/vervangendvervoer/VervangendVervoerPage'), 'VervangendVervoerListPage');
const VervangendVervoerCreatePage = lazyScreen(() => import('@/features/inrichting/vervangendvervoer/VervangendVervoerPage'), 'VervangendVervoerCreatePage');
const VervangendVervoerEditPage = lazyScreen(() => import('@/features/inrichting/vervangendvervoer/VervangendVervoerPage'), 'VervangendVervoerEditPage');
const VervangendVervoerDeletePage = lazyScreen(() => import('@/features/inrichting/vervangendvervoer/VervangendVervoerPage'), 'VervangendVervoerDeletePage');
const MonteursListRoute = lazyScreen(() => import('@/features/inrichting/monteurs/MonteursPage'), 'MonteursListRoute');
const MonteurCreateRoute = lazyScreen(() => import('@/features/inrichting/monteurs/MonteursPage'), 'MonteurCreateRoute');
const MonteurEditRoute = lazyScreen(() => import('@/features/inrichting/monteurs/MonteursPage'), 'MonteurEditRoute');
const CustomDossierTypesListPage = lazyScreen(() => import('@/features/inrichting/customDossierTypes/CustomDossierTypesPage'), 'CustomDossierTypesListPage');
const CustomDossierTypeCreatePage = lazyScreen(() => import('@/features/inrichting/customDossierTypes/CustomDossierTypesPage'), 'CustomDossierTypeCreatePage');
const CustomDossierTypeEditPage = lazyScreen(() => import('@/features/inrichting/customDossierTypes/CustomDossierTypesPage'), 'CustomDossierTypeEditPage');
const CustomDossierTypeDeletePage = lazyScreen(() => import('@/features/inrichting/customDossierTypes/CustomDossierTypesPage'), 'CustomDossierTypeDeletePage');
const ChecklistPartsPage = lazyScreen(() => import('@/features/inrichting/checklistParts/ChecklistPartsPage'), 'ChecklistPartsPage');
const MessageTemplatesListPage = lazyScreen(() => import('@/features/inrichting/messageTemplates/MessageTemplatesPage'), 'MessageTemplatesListPage');
const MessageTemplateCreatePage = lazyScreen(() => import('@/features/inrichting/messageTemplates/MessageTemplatesPage'), 'MessageTemplateCreatePage');
const MessageTemplateEditPage = lazyScreen(() => import('@/features/inrichting/messageTemplates/MessageTemplatesPage'), 'MessageTemplateEditPage');
const MailboxenListPage = lazyScreen(() => import('@/features/inrichting/mailboxen/MailboxenPage'), 'MailboxenListPage');
const MailboxenCreatePage = lazyScreen(() => import('@/features/inrichting/mailboxen/MailboxenPage'), 'MailboxenCreatePage');
const MailboxenEditPage = lazyScreen(() => import('@/features/inrichting/mailboxen/MailboxenPage'), 'MailboxenEditPage');
const MailboxenDeletePage = lazyScreen(() => import('@/features/inrichting/mailboxen/MailboxenPage'), 'MailboxenDeletePage');
const SystemMessagesListPage = lazyScreen(() => import('@/features/system/messages/SystemMessagesPage'), 'SystemMessagesListPage');
const SystemMessagesCreatePage = lazyScreen(() => import('@/features/system/messages/SystemMessagesPage'), 'SystemMessagesCreatePage');
const SystemMessagesEditPage = lazyScreen(() => import('@/features/system/messages/SystemMessagesPage'), 'SystemMessagesEditPage');
const SystemMessagesDeletePage = lazyScreen(() => import('@/features/system/messages/SystemMessagesPage'), 'SystemMessagesDeletePage');
const DeclarationChainsPage = lazyScreen(() => import('@/features/system/declarationparties/DeclarationChainsPage'), 'DeclarationChainsPage');
const DeclarationChainDetailPage = lazyScreen(() => import('@/features/system/declarationparties/DeclarationChainDetailPage'), 'DeclarationChainDetailPage');
const DeclarationBranchDetailPage = lazyScreen(() => import('@/features/system/declarationparties/DeclarationBranchDetailPage'), 'DeclarationBranchDetailPage');
const DeclarationSubmissionsPage = lazyScreen(() => import('@/features/system/declarationsubmissions/DeclarationSubmissionsPage'), 'DeclarationSubmissionsPage');
const ConfigurationPage = lazyScreen(() => import('@/features/system/configuration/ConfigurationPage'), 'ConfigurationPage');
const MenuSettingsPage = lazyScreen(() => import('@/features/system/menu/MenuSettingsPage'), 'MenuSettingsPage');
const PartnersPage = lazyScreen(() => import('@/features/system/partners/PartnersPage'), 'PartnersPage');
const TraceLogPage = lazyScreen(() => import('@/features/system/log/TraceLogPage'), 'TraceLogPage');
const TraceLogDetailPage = lazyScreen(() => import('@/features/system/log/TraceLogDetailPage'), 'TraceLogDetailPage');
const SystemUsersPage = lazyScreen(() => import('@/features/system/users/SystemUsersPage'), 'SystemUsersPage');
const SystemUserDetailPage = lazyScreen(() => import('@/features/system/users/SystemUserDetailPage'), 'SystemUserDetailPage');
const AnnotationEditorRoute = lazyScreen(() => import('@/features/dossiers/attachments/annotations/AnnotationEditorRoute'), 'AnnotationEditorRoute');
const ProfilePage = lazyScreen(() => import('@/features/profile/ProfilePage'), 'ProfilePage');
const SupportRequestsPage = lazyScreen(() => import('@/features/support/SupportRequestsPage'), 'SupportRequestsPage');
const SupportRequestDetailPage = lazyScreen(() => import('@/features/support/SupportRequestDetailPage'), 'SupportRequestDetailPage');
const SupportInboxPage = lazyScreen(() => import('@/features/support/inbox/SupportInboxPage'), 'SupportInboxPage');
const SupportInboxDetailPage = lazyScreen(() => import('@/features/support/inbox/SupportInboxDetailPage'), 'SupportInboxDetailPage');
const SupportTypesPage = lazyScreen(() => import('@/features/support/types/SupportTypesPage'), 'SupportTypesPage');
const KladblokPage = lazyScreen(() => import('@/features/calculation/kladblok/KladblokPage'), 'KladblokPage');
const KlantenPage = lazyScreen(() => import('@/features/relaties/KlantenPage'), 'KlantenPage');
const KlantDetailPage = lazyScreen(() => import('@/features/relaties/KlantDetailPage'), 'KlantDetailPage');
const ImportRelationsPage = lazyScreen(() => import('@/features/import/ImportRelationsPage'), 'ImportRelationsPage');
const VerzekeraarsPage = lazyScreen(() => import('@/features/verzekeraars/VerzekeraarsPage'), 'VerzekeraarsPage');
const VerzekeraarDetailPage = lazyScreen(() => import('@/features/verzekeraars/VerzekeraarDetailPage'), 'VerzekeraarDetailPage');
const RepairersPage = lazyScreen(() => import('@/features/repairers/RepairersPage'), 'RepairersPage');
const DealerSurchargePage = lazyScreen(() => import('@/features/dealerSurcharge/DealerSurchargePage'), 'DealerSurchargePage');
const DealerSurchargeInsurerPage = lazyScreen(() => import('@/features/dealerSurcharge/DealerSurchargeInsurerPage'), 'DealerSurchargeInsurerPage');
const DealerSurchargeExchangePage = lazyScreen(() => import('@/features/dealerSurcharge/DealerSurchargeExchangePage'), 'DealerSurchargeExchangePage');
const RepairerDetailPage = lazyScreen(() => import('@/features/repairers/RepairerDetailPage'), 'RepairerDetailPage');
const NewRepairerPage = lazyScreen(() => import('@/features/repairers/NewRepairerPage'), 'NewRepairerPage');
const ItemsPage = lazyScreen(() => import('@/features/items/ItemsPage'), 'ItemsPage');
const ItemDetailPage = lazyScreen(() => import('@/features/items/ItemDetailPage'), 'ItemDetailPage');
const NewItemPage = lazyScreen(() => import('@/features/items/NewItemPage'), 'NewItemPage');
const BestellijstPage = lazyScreen(() => import('@/features/inkoop/BestellijstPage'), 'BestellijstPage');
const OrdersPage = lazyScreen(() => import('@/features/inkoop/orders/OrdersPage'), 'OrdersPage');
const OrderDetailPage = lazyScreen(() => import('@/features/inkoop/orders/OrderDetailPage'), 'OrderDetailPage');
const WarehousesPage = lazyScreen(() => import('@/features/inventory/WarehousesPage'), 'WarehousesPage');
const CountImportPage = lazyScreen(() => import('@/features/inventory/CountImportPage'), 'CountImportPage');
const FacturatieDashboardPage = lazyScreen(() => import('@/features/facturatie/FacturatieDashboardPage'), 'FacturatieDashboardPage');
const FacturatieExportPage = lazyScreen(() => import('@/features/facturatie/FacturatieExportPage'), 'FacturatieExportPage');
const FacturenOverzichtPage = lazyScreen(() => import('@/features/facturatie/FacturenOverzichtPage'), 'FacturenOverzichtPage');
const FactuurDetailPage = lazyScreen(() => import('@/features/facturatie/FactuurDetailPage'), 'FactuurDetailPage');
const GrootboekenPage = lazyScreen(() => import('@/features/facturatie/GrootboekenPage'), 'GrootboekenPage');
const ExportinstellingenPage = lazyScreen(() => import('@/features/facturatie/ExportinstellingenPage'), 'ExportinstellingenPage');
const NummeringPage = lazyScreen(() => import('@/features/facturatie/NummeringPage'), 'NummeringPage');
const InvoiceDocumentsPage = lazyScreen(() => import('@/features/facturatie/InvoiceDocumentsPage'), 'InvoiceDocumentsPage');
const BillingRunsPage = lazyScreen(() => import('@/features/billingRuns/BillingRunsPage'), 'BillingRunsPage');
const BillingRunDetailPage = lazyScreen(() => import('@/features/billingRuns/BillingRunDetailPage'), 'BillingRunDetailPage');
const BillingRunNewPage = lazyScreen(() => import('@/features/billingRuns/BillingRunNewPage'), 'BillingRunNewPage');
const DocumentationPage = lazyScreen(() => import('@/features/documentation/DocumentationPage'), 'DocumentationPage');
const NewsMenuPage = lazyScreen(() => import('@/features/news/NewsMenuPage'), 'NewsMenuPage');
const NewsAdminListPage = lazyScreen(() => import('@/features/news/NewsAdminListPage'), 'NewsAdminListPage');
const NewsEditPage = lazyScreen(() => import('@/features/news/NewsEditPage'), 'NewsEditPage');
const RapportageAppPage = lazyScreen(() => import('@/features/rapportage/RapportageAppPage'), 'RapportageAppPage');
const RapportParamsPage = lazyScreen(() => import('@/features/rapportage/RapportParamsPage'), 'RapportParamsPage');
const RapportageMenuPerRolePage = lazyScreen(() => import('@/features/rapportage/admin/RapportageMenuPerRolePage'), 'RapportageMenuPerRolePage');
const ClaimFilterOverviewPage = lazyScreen(() => import('@/features/claimfilter/ClaimFilterOverviewPage'), 'ClaimFilterOverviewPage');
const ClaimFilterRoundsPage = lazyScreen(() => import('@/features/claimfilter/ClaimFilterRoundsPage'), 'ClaimFilterRoundsPage');
const ClaimFilterDossiersPage = lazyScreen(() => import('@/features/claimfilter/ClaimFilterDossiersPage'), 'ClaimFilterDossiersPage');
const ClaimFilterHealthPage = lazyScreen(() => import('@/features/claimfilter/ClaimFilterHealthPage'), 'ClaimFilterHealthPage');
const ClaimFilterRecordsPage = lazyScreen(() => import('@/features/claimfilter/ClaimFilterRecordsPage'), 'ClaimFilterRecordsPage');
const ClaimFilterRecordDetailPage = lazyScreen(() => import('@/features/claimfilter/ClaimFilterRecordDetailPage'), 'ClaimFilterRecordDetailPage');
// Story fe-be-claimfilter-oordeelsregels-beheer (2026-09-04): de maatschappij richt
// haar eigen bandbreedtes in.
const ClaimFilterRulesPage = lazyScreen(() => import('@/features/claimfilterrules/ClaimFilterRulesPage'), 'ClaimFilterRulesPage');
const LeveranciersListPage = lazyScreen(() => import('@/features/inkoop/leveranciers/LeveranciersPage'), 'LeveranciersListPage');
const LeveranciersCreatePage = lazyScreen(() => import('@/features/inkoop/leveranciers/LeveranciersPage'), 'LeveranciersCreatePage');
const LeveranciersEditPage = lazyScreen(() => import('@/features/inkoop/leveranciers/LeveranciersPage'), 'LeveranciersEditPage');
const LeveranciersDeletePage = lazyScreen(() => import('@/features/inkoop/leveranciers/LeveranciersPage'), 'LeveranciersDeletePage');
const InternalSupplierStockPage = lazyScreen(() => import('@/features/inkoop/internalSupplier/InternalSupplierStockPage'), 'InternalSupplierStockPage');
const InternalSupplierImportPage = lazyScreen(() => import('@/features/inkoop/internalSupplier/InternalSupplierImportPage'), 'InternalSupplierImportPage');

/**
 * De kantoorschil (`Chrome`) — ook lazy (S021.5b §1.1). Samen met de RuitID-poll: die
 * provider hing vóór deze story boven de router in `App.tsx` en zat daardoor op `/monteur`
 * in de geladen code (AC 2). Hij leeft nu rond de kantoorschil. Navigeren tussen
 * kantoorschermen houdt deze layout gemount, dus de poll overleeft de navigatie zoals
 * voorheen (S014.4); alleen buiten de kantoorschil (`/monteur`, `/login`) is hij er niet.
 * De naam blijft `Chrome` (TC-FE-GRD-06 herkent de schil eraan).
 */
const Chrome = lazyScreen(
    async () => {
        const [{ Chrome: OfficeChrome }, { RuitIdPollProvider }] = await Promise.all([
            import('@/components/chrome/Chrome'),
            import('@/features/dossiers/ruitid/RuitIdPollProvider'),
        ]);
        /* Demping (2026-06-14, ongewijzigd uit App.tsx): de AutoQraft-callback is de
           primaire afrond-route, de poll is een backstop → 15s, en hoogstens 60 polls
           (≈ 15 min) per aanvraag. */
        function ChromeWithRuitIdPoll() {
            return (
                <RuitIdPollProvider pollIntervalMs={15000} maxPolls={60}>
                    <OfficeChrome />
                </RuitIdPollProvider>
            );
        }
        return { Chrome: ChromeWithRuitIdPoll };
    },
    'Chrome',
    true,
);

/**
 * Verhuizing artikeltypes+prijslijsten uit Inrichting naar de eigen
 * Artikelbeheer-app (2026-06-24): herschrijft een oude pad-prefix naar de
 * nieuwe locatie met behoud van het sub-pad + querystring, zodat bestaande
 * bookmarks (`/inrichting/prijslijsten/:id/bewerken`) blijven werken.
 * `replace` zodat de oude URL niet in de history blijft hangen.
 */
export function PrefixRedirect({ from, to }: { from: string; to: string }) {
    const { pathname, search } = useLocation();
    const rest = pathname.startsWith(from) ? pathname.slice(from.length) : '';
    return <Navigate to={`${to}${rest}${search}`} replace />;
}

/**
 * Story `fe-be-verzekeraar-rol-scope-worklist` (2026-06-29): de landing na login.
 * Een verzekeraar landt op de worklist "Te beoordelen"; elke andere
 * (interne/system) gebruiker behoudt de bestaande dashboard-landing.
 */
export function DefaultLanding() {
    const { user } = useAuth();
    if (isInsurerUser(user)) return <Navigate to="/te-beoordelen" replace />;
    // Story fe-be-cursusmodus-hersteller-mvp (AC-C1.4): een cursist zonder voortgang landt op
    // /cursussen. Alleen bij `canUseCourses` — op productie verandert hier niets en wordt er
    // niets extra geladen of gelezen. De keuze zelf (voortgang in de browser) zit in een eigen
    // lazy scherm, want de voortgang hoort niet in de keten van /monteur.
    if (canUseCourses(user)) return <CourseLanding />;
    return <Navigate to="/dashboard" replace />;
}

/** Story fe-be-cursusmodus-hersteller-mvp (AC-C1.4): /cursussen of /dashboard, op de voortgang. */
const CourseLanding = lazyScreen(() => import('@/features/training/CourseLanding'), 'CourseLanding');

/** Story fe-be-cursusmodus-hersteller-mvp (AC-C1.3): het startscherm "Cursussen". */
const CoursesPage = lazyScreen(() => import('@/features/training/CoursesPage'), 'CoursesPage');

/**
 * Story fe-be-cursusmodus-hersteller-mvp (AC-C1.2, A3.4): `/cursussen` bestaat alleen bij
 * `canUseCourses(user)`. Anders (bookmark, of de cursusmodus ging tijdens de sessie uit) naar
 * `/dashboard`, vóór het scherm geladen wordt.
 */
export function CoursesRoute() {
    const { user } = useAuth();
    if (!canUseCourses(user)) return <Navigate to="/dashboard" replace />;
    return <CoursesPage />;
}

/**
 * Story `fe-be-monteur-s1-basis` (EPIC-021, S021.1): de monteur-weergave als eigen
 * LAZY chunk, zodat `/monteur` de schil los laadt. Hij hangt binnen `<ProtectedRoute>`
 * maar BUITEN `<Chrome>`. Sinds S021.5b via `lazyScreen`, zodat ook een ontbrekende
 * monteur-chunk na een deploy één keer herlaadt (§1.2, "kantoor én monteur").
 */
const FitterShell = lazyScreen(() => import('@/features/fitter/FitterShell'), 'default', true);

export function FitterShellRoute() {
    return <FitterShell />;
}

/** S021.5b §3: het monteur-inlogscherm (`/monteur/inloggen`), eigen lazy chunk onder features/fitter. */
const FitterLoginRoute = lazyScreen(() => import('@/features/fitter/login/FitterLoginPage'), 'FitterLoginPage', true);

/*
 * Story be-fe-wachtwoord-vergeten — "wachtwoord vergeten" (AC 1/8/19). Publiek: buiten <ProtectedRoute>.
 * Kantoor via `@/features/auth/passwordReset/` (lazy, nooit in de keten van /monteur); monteur onder
 * features/fitter/login (lazy, in de keten en de precache van /monteur, net als het inlogscherm).
 */
const ForgotPasswordRoute = lazyScreen(() => import('@/features/auth/passwordReset/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordRoute = lazyScreen(() => import('@/features/auth/passwordReset/ResetPasswordPage'), 'ResetPasswordPage');
const FitterForgotPasswordRoute = lazyScreen(
    () => import('@/features/fitter/login/FitterForgotPasswordPage'),
    'FitterForgotPasswordPage',
    true,
);
const FitterResetPasswordRoute = lazyScreen(
    () => import('@/features/fitter/login/FitterResetPasswordPage'),
    'FitterResetPasswordPage',
    true,
);

export const routes: RouteObject[] = [
  {
    path: '/login',
    /* Flag-gated pilot-login: default UIT → werkende `LoginPage`;
     * `VITE_AUTH_FLOW=true` → multi-step `LoginFlowPage`. Zie LoginRoute. */
    element: <LoginRoute />,
  },
  /* Story fe-be-monteur-s5b-installeerbaar (EPIC-021, S021.5b §3, concept 1a): het
   * inlogscherm van de monteur-app, BUITEN <ProtectedRoute> maar BINNEN de scope /monteur
   * (vondst 3: anders toont de geïnstalleerde app een adresbalk bij het inloggen). Een
   * statisch pad wint van `monteur/*` hieronder, dus geen redirect-lus (TC-FE-RDR-07).
   * Lazy en onder features/fitter/: daarmee zit hij in de keten van /monteur en in de
   * precache van de service worker (review A-5). */
  { path: '/monteur/inloggen', element: <FitterLoginRoute /> },
  /* be-fe-wachtwoord-vergeten: publieke schermen (geen ProtectedRoute). De monteur-paden zijn statisch en winnen
   * dus van `monteur/*` hieronder. */
  { path: '/wachtwoord-vergeten', element: <ForgotPasswordRoute /> },
  { path: '/wachtwoord-herstellen', element: <ResetPasswordRoute /> },
  { path: '/monteur/wachtwoord-vergeten', element: <FitterForgotPasswordRoute /> },
  { path: '/monteur/wachtwoord-herstellen', element: <FitterResetPasswordRoute /> },
  /* TIJDELIJK — verwijderen in S010.7 (cleanup) of zodra alle tokens
   * elders in productie gebruikt worden. Geen menu-link; alleen via
   * directe URL `/dev/tokens-preview` bereikbaar. */
  {
    path: '/dev/tokens-preview',
    element: <TokensPreviewPage />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      /* Story fe-be-monteur-s1-basis (EPIC-021, S021.1, B-1): de monteur-weergave op
       * het bestaande adres van de instantie. Binnen <ProtectedRoute> (wie niet is
       * ingelogd gaat naar /login en daarna terug naar /monteur — state.from), maar
       * BUITEN <Chrome>: eigen schil, eigen lazy chunk. */
      { path: 'monteur/*', element: <FitterShellRoute /> },
      /* Story fe-be-monteur-s1-basis (S021.1): de FitterOnlyRouteGuard staat DIRECT
       * boven <Chrome>, dus vóór InsurerRouteGuard en RestrictedRepairerRouteGuard.
       * Een alleen-monteur (of een sessie met een onbekende monteur-waarde) gaat naar
       * /monteur zonder dat <Chrome> mount — anders vuurt Chrome z'n eigen calls af,
       * die voor een alleen-monteur allemaal 403 geven. Eén guard voor elke ingang
       * (DefaultLanding na inloggen, bookmarks, bel-links, logo, de *-fallback). */
      {
        element: <FitterOnlyRouteGuard />,
        children: [
        /* Story be-fe-contactgegevens-bevestigen (AC 7): zolang whoami `contactVerificationRequired` meldt, staat
         * hier in plaats van elke kantoorpagina het scherm "Vul je contactgegevens aan" — vóór <Chrome>, zodat de
         * schil (en haar calls) niet mount. Onder de FitterOnlyRouteGuard: een alleen-monteur krijgt op /monteur
         * de monteur-variant (poort in FitterShell). */
        {
          element: <ContactVerificationRouteGuard />,
          children: [
      {
        element: <Chrome />,
        children: [
          /* Story fe-be-verzekeraar-rol-scope-worklist (2026-06-29): de
           * InsurerRouteGuard houdt de verzekeraar-rol binnen z'n kale ervaring
           * — een verzekeraar die een interne app-route intypt wordt naar
           * "Te beoordelen" geredirect (open punt 6). Niet-verzekeraars passeren
           * ongemoeid (geen regressie). De guard is een layout-route die ALLE
           * gechromede children omsluit. */
          {
            element: <InsurerRouteGuard />,
            children: [
          /* Story fe-be-verzekeraar-gebonden-hersteller-chrome-tabs (EPIC-019
           * S-C, beslissing C1, 2026-08-21): dezelfde vorm één laag dieper — een
           * BEPERKTE hersteller die op een route van een verborgen app landt
           * (bookmark, bel-menu-deeplink, credit-link, logo, PrefixRedirect)
           * gaat naar het Glas-dashboard. Eén guard sluit alle vier de
           * navigatie-gaten (N1-N4). Niet-beperkte gebruikers passeren
           * ongemoeid; de <Navigate> zit in de guard, niet hier. */
          {
            element: <RestrictedRepairerRouteGuard />,
            children: [
          /* Story dashboard-fase1-hersteller (2026-06-12): het rol-aware
           * dashboard is de landing voor interne users; de verzekeraar landt op
           * "Te beoordelen" (DefaultLanding kiest op rol). */
          { index: true, element: <DefaultLanding /> },
          /* Story fe-be-verzekeraar-rol-scope-worklist (2026-06-29): de
           * verzekeraar-worklist. Hard rol-gegate (alleen 'verzekeraar') zodat
           * een interne user die de URL intypt naar `/` wordt teruggestuurd. */
          {
            element: <ProtectedRoute roles={['verzekeraar']} />,
            children: [
              { path: 'te-beoordelen', element: <InsurerWorklistPage /> },
              /* Story fe-be-verzekeraar-dossiers-zoeken (2026-06-30): het tweede
               * menu-item "Dossiers" — zoekscherm over ALLE statussen + archief
               * binnen de verzekeraar-scope. Child van /te-beoordelen (valt al in
               * INSURER_ALLOWED_PREFIXES); botst niet met de hersteller-/dossiers. */
              { path: 'te-beoordelen/dossiers', element: <InsurerDossiersPage /> },
            ],
          },
          /* Story fe-be-claimfilter-oordeelsregels-beheer (2026-09-04), verhuisd naar
           * top-level op 09-09-2026 toen de verzekeraar erbij kwam.
           *
           * ⚠⚠ HIJ HANGT MET OPZET BUITEN ELKE APP-GROEP. Onder `/claimfilter/*` zou de
           *    system/support-poort van die app de verzekeraar buitensluiten; onder de
           *    `roles={['verzekeraar']}`-groep hierboven zouden system en support eruit
           *    vallen. Twee publieken, één scherm — dus geen FE-rolpoort, en het BACKEND
           *    bepaalt wat ieder ziet: `ResolveInsurerIds()` geeft system/support alle
           *    maatschappijen en de verzekeraar uitsluitend die van haar eigen bedrijf,
           *    met een 404 (niet 403) buiten scope zodat andermans bestaan niet lekt.
           *
           * ⚠ De verzekeraar bereikt hem alleen wanneer `/oordeelsregels` óók in
           *   INSURER_ALLOWED_PREFIXES staat — anders stuurt die guard haar STIL terug
           *   naar "Te beoordelen" en lijkt de link stuk. Die twee horen bij elkaar. */
          { path: 'oordeelsregels', element: <ClaimFilterRulesPage /> },
          { path: 'dashboard', element: <DashboardPage /> },
          /* Story fe-be-cursusmodus-hersteller-mvp (AC-C1.2): het startscherm "Cursussen",
           * alleen bij canUseCourses (eigen poort in CoursesRoute, vóór het laden). */
          { path: 'cursussen', element: <CoursesRoute /> },
          /* Story fe-mijn-gegevens-scherm (S-B) — self-service profiel met
           * tab-shell; de actieve tab staat in de URL. */
          { path: 'mijn-gegevens', element: <Navigate to="/mijn-gegevens/identiteit" replace /> },
          { path: 'mijn-gegevens/:tab', element: <ProfilePage /> },
          /* Story be-fe-support-melden-en-volgen (S1, AC 35/36): Mijn supportmeldingen. Hangt in
           * geen app, dus de gebonden-hersteller-guard laat hem door; de verzekeraar-guard kent
           * '/support' in zijn allowlist (AC 37). */
          { path: 'support/requests', element: <SupportRequestsPage /> },
          { path: 'support/requests/:number', element: <SupportRequestDetailPage /> },
          /* Story be-fe-support-app-behandelen (S2, AC 4): de app Support (behandelaar). Geen FE-rolpoort: de BE beslist
           * (403/503) en het scherm toont dan zijn eigen foutzin — nooit een lege lijst. */
          { path: 'support/inbox', element: <SupportInboxPage /> },
          { path: 'support/inbox/:number', element: <SupportInboxDetailPage /> },
          { path: 'dossiers', element: <DossiersPage /> },
          { path: 'dossiers/:id', element: <DossierDetailPage /> },
          /* Story fe-calculatie-kladblok-glas (2026-06-23): losse calculatie-
           * pagina ("kladblok") onder de glas-app. Rekent zonder dossier; niets
           * persisteert tot "Maak dossier" (Story 2). */
          { path: 'calculatie', element: <KladblokPage /> },
          /* Story fe-be-meldingen-overzicht (2026-06-14): het meldingen-
           * overzicht (lijst/zoekscherm) — entry-point vanuit het menu. */
          { path: 'intakes', element: <MeldingenPage /> },
          /* Story be-fe-callcenter-melding-aanmaken (09-10-2026): het callcenter
           * neemt buiten kantooruren een melding aan — de vervanger van
           * Glas\SzaCall.aspx.
           * ⚠ Deze route staat BEWUST vóór 'intakes/:nummer': anders vangt die
           *   parameter-route het woord "aannemen" op als meldingnummer. */
          { path: 'intakes/aannemen', element: <CallCenterIntakePage /> },
          /* 10-10-2026: het meldingnummer hoort in de URL. Zonder dat staat het
           * nummer alleen in het geheugen van de pagina — ververst de medewerker
           * midden in een gesprek, dan is het scherm leeg terwijl de melding wél
           * bestaat, en is er geen weg terug naar dat nummer behalve de lijst.
           * ⚠ Ook deze route staat vóór 'intakes/:nummer'. */
          { path: 'intakes/aannemen/:nummer', element: <CallCenterIntakePage /> },
          /* Story fe-be-melding-hersteller-accept-weiger (2026-06-13):
           * hersteller-melding-detail (accepteren/weigeren). Entry-point komt
           * uit het meldingen-overzicht of een mail-link. */
          { path: 'intakes/:nummer', element: <IntakeDetailPage /> },
          /* Story fe-relatiebeheer-fase1-A (2026-06-23): de klantenlijst van de
           * nieuwe Relatiebeheer-app. De detail-route /klanten/:id komt in
           * Story B; de rij-link wijst er nu al naartoe (tot dan: 404-fallback
           * via de wildcard-redirect). GÉÉN rol-gate — open voor elke
           * ingelogde user, veiligheid via BE company-scope (RelationsController
           * = [SessionAuth] + OwnerID-scope). */
          { path: 'klanten', element: <KlantenPage /> },
          /* Story fe-relatiebeheer-fase1-B (2026-06-23): klant-detailscherm
           * (rail + tabs, Relatie-tab read-only). GÉÉN rol-gate — open voor elke
           * ingelogde user; veiligheid via BE company-scope (RelationsController
           * = [SessionAuth] + OwnerID-scope, 404 bij cross-tenant). */
          /* Story fe-importraamwerk-fase1-inlezen-kolommatch (S1, 2026-09-10):
           * fase 1 van de relatie-import (bestand inlezen + kolom-match).
           *
           * ⚠ De route ligt met OPZET onder het `/klanten`-prefix (AC1a): de
           *   poort is "wie /klanten mag zien, mag importeren"
           *   (eigenaarsbesluit OV-4), en `hiddenPrefixesForRestrictedRepairer`
           *   LEIDT de verborgen prefixen af uit APPS. `/klanten` staat daar al
           *   in, dus `/klanten/importeren` valt automatisch onder de bestaande
           *   RestrictedRepairerRouteGuard — nul extra gate, nul backend. Een
           *   route BUITEN dat prefix (`/relaties-importeren`) zou een tweede,
           *   handgeschreven gate nodig hebben en daarmee stil uit de pas lopen.
           *
           * De statische route staat vóór `klanten/:id` zodat het segment
           * `importeren` niet als relatie-id wordt gelezen.
           */
          { path: 'klanten/importeren', element: <ImportRelationsPage /> },
          { path: 'klanten/:id', element: <KlantDetailPage /> },
          /* Story fe-artikelbeheer-zoek-detail-scoping (2026-06-24): Artikelbeheer
           * — Zoek-tab + artikel-detail-shell + aanmaken. GÉÉN rol-gate — bekijken
           * = iedereen; bewerken/aanmaken is BE-flag-gestuurd (canEdit +
           * owner-check 403). De `nieuw`-route staat vóór `:id` (statische route
           * wint, maar expliciet voor de leesbaarheid).
           * Verhuizing 2026-06-24: routes onder het nieuwe prefix
           * `/artikelbeheer/artikelen/*` (was `/artikelen/*`). */
          { path: 'artikelbeheer/artikelen', element: <ItemsPage /> },
          { path: 'artikelbeheer/artikelen/nieuw', element: <NewItemPage /> },
          { path: 'artikelbeheer/artikelen/:id', element: <ItemDetailPage /> },
          /* Story fe-relatiebeheer-verzekeraars-fase-a1 (2026-06-24):
           * verzekeraarslijst + read-only detail van de Relatiebeheer-app. GÉÉN
           * rol-gate — bekijken = iedereen; veiligheid via BE (SessionAuth +
           * 404 bij onbestaande id). Aanmaak/edit-affordances zijn BE-flag-
           * gestuurd (canManageInsurer/canEditDebtor). */
          { path: 'verzekeraars', element: <VerzekeraarsPage /> },
          { path: 'verzekeraars/:id', element: <VerzekeraarDetailPage /> },
          /* Story be-fe-relatiebeheer-herstellers (2026-10-09, increment A): herstellerlijst
           * + detail. GÉÉN FE-rol-gate op de route — de BE beslist (403/404/503) en de
           * pagina toont dan het juiste foutscherm (A-38). Het menu-item is gegate op
           * whoami `canManageRepairers`. */
          { path: 'herstellers', element: <RepairersPage /> },
          /* Increment D: de wizard "Nieuwe hersteller" — statisch, VÓÓR herstellers/:id (anders is "nieuw" een id).
           * Geen FE-rol-gate: de wizard toont bij 403/503 het foutscherm van A-38 (D-1). */
          { path: 'herstellers/nieuw', element: <NewRepairerPage /> },
          { path: 'herstellers/:id', element: <RepairerDetailPage /> },
          /* Story be-fe-meerprijs-dealer-kortingen (26-10-2026): Relatiebeheer → Meerprijs dealer. BEIDE routes achter
           * whoami `canManageDealerSurcharge` (vlag aan én System of keten-HK); elk api/dealer-surcharge-endpoint toetst
           * zelf opnieuw (404 bij een uitgezette vlag, 403/503 anders). */
          {
            element: <ProtectedRoute canManageDealerSurcharge />,
            children: [
              { path: 'meerprijs-dealer', element: <DealerSurchargePage /> },
              /* Story be-fe-meerprijs-dealer-exchange-keten (M-6): de eXchange-set — STATISCH naast :companyId (react-router
               * kiest de statische route), dus nooit een aanroep van insurers/exchange. */
              { path: 'meerprijs-dealer/exchange', element: <DealerSurchargeExchangePage /> },
              { path: 'meerprijs-dealer/:companyId', element: <DealerSurchargeInsurerPage /> },
            ],
          },
          /* EPIC-012 Planbord — story S012.2 (read-only week/dag-view). */
          { path: 'planbord', element: <PlanbordPage /> },
          /* EPIC-012 Planbord — story S012.4 (beschikbaarheid-inrichting). */
          { path: 'planbord/inrichting', element: <PlanbordSettingsPage /> },
          /* Story annotaties-mvp (S-BL.2, 2026-05-30). */
          {
            path: 'dossiers/:dossierId/bijlagen/:attId/annoteer',
            element: <AnnotationEditorRoute />,
          },
          /* Verhuizing artikeltypes+prijslijsten uit Inrichting naar de eigen
           * Artikelbeheer-app (2026-06-24): routes onder het nieuwe prefix
           * `/artikelbeheer/*`. Source-folders blijven staan
           * (`features/inrichting/itemtypes` + `.../pricelists`); alleen de
           * route-paden + menu verhuizen. */
          { path: 'artikelbeheer/artikeltypes', element: <ItemTypesPage /> },
          { path: 'artikelbeheer/artikeltypes/nieuw', element: <ItemTypeEditPage mode="create" /> },
          { path: 'artikelbeheer/artikeltypes/:id', element: <ItemTypeDetailPage /> },
          { path: 'artikelbeheer/artikeltypes/:id/bewerken', element: <ItemTypeEditPage mode="edit" /> },
          { path: 'artikelbeheer/artikeltypes/:id/verwijderen', element: <ItemTypeDeletePage /> },
          { path: 'artikelbeheer/prijslijsten', element: <PriceListsPage /> },
          { path: 'artikelbeheer/prijslijsten/nieuw', element: <PriceListEditPage mode="create" /> },
          { path: 'artikelbeheer/prijslijsten/:id', element: <PriceListDetailPage /> },
          { path: 'artikelbeheer/prijslijsten/:id/bewerken', element: <PriceListEditPage mode="edit" /> },
          { path: 'artikelbeheer/prijslijsten/:id/verwijderen', element: <PriceListDeletePage /> },
          /* Bookmark-redirects: oude Inrichting-paden → nieuwe Artikelbeheer-
           * locatie (binnen de Chrome-children, dus ook gechromed). De
           * `from`-prefix blijft `/inrichting/...` — niet meeverhuizen. */
          {
              path: 'inrichting/artikeltypes/*',
              element: <PrefixRedirect from="/inrichting/artikeltypes" to="/artikelbeheer/artikeltypes" />,
          },
          {
              path: 'inrichting/prijslijsten/*',
              element: <PrefixRedirect from="/inrichting/prijslijsten" to="/artikelbeheer/prijslijsten" />,
          },
          /* Story fotoprotocol-beheer-v1 (2026-05-29). */
          { path: 'inrichting/fotoprotocollen', element: <PhotoProtocolListScreen /> },
          /* Story fe-be-vervangend-vervoer-voertuigen (2026-06-29): beheer van de
           * voertuigen (UA_RentalCar) die een hersteller als vervangend vervoer
           * meegeeft. GÉÉN rol-gate — spiegelt Fotoprotocollen/Leveranciers
           * (gewone authenticated route): veiligheid via BE company-/hersteller-
           * scoping (RentalCarsController = [SessionAuth] + scope). */
          { path: 'inrichting/vervangend-vervoer', element: <VervangendVervoerListPage /> },
          { path: 'inrichting/vervangend-vervoer/nieuw', element: <VervangendVervoerCreatePage /> },
          { path: 'inrichting/vervangend-vervoer/:id/bewerken', element: <VervangendVervoerEditPage /> },
          { path: 'inrichting/vervangend-vervoer/:id/verwijderen', element: <VervangendVervoerDeletePage /> },
          /* Story fe-monteurs-beheer-crud (2026-07-01, story 1): monteur-CRUD
           * (UA_Fitter) + optioneel een bestaande gebruiker koppelen. Eigen
           * "Monteurs"-scherm in de Inrichting-app. GÉÉN rol-gate — spiegelt
           * Vervangend vervoer/Fotoprotocollen (gewone authenticated route);
           * veiligheid via BE company-scoping (FittersController = [SessionAuth]
           * + OwnerID-scope). */
          { path: 'inrichting/monteurs', element: <MonteursListRoute /> },
          { path: 'inrichting/monteurs/nieuw', element: <MonteurCreateRoute /> },
          { path: 'inrichting/monteurs/:id/bewerken', element: <MonteurEditRoute /> },
          /* Story fe-be-monteur-s6-onderdelen (S021.6 deel C): de onderdelenlijst van de eigen
           * hersteller. BEWUST geen <ProtectedRoute repairersOnly>: wie het adres direct opent zonder
           * hersteller (bv. system), krijgt van de BE 403 no_repairer_context en ziet op de pagina de
           * uitleg "gebruik Login als vestiging" (concept 5d) in plaats van een stille redirect. De tab
           * zelf staat alleen voor herstellers in de Inrichting-app (requiresRepairerContext). */
          { path: 'inrichting/onderdelen', element: <ChecklistPartsPage /> },
          /* Story be-fe-eigen-dossiersoorten: de eigen dossiersoorten (UA_CustomDossierType) van de
           * eigen vestiging. Net als Onderdelenlijst BEWUST zonder repairersOnly-guard: zonder
           * vestiging geeft de BE 403 no_repairer_context en toont de lijst "Login als vestiging". */
          { path: 'inrichting/eigen-dossiersoorten', element: <CustomDossierTypesListPage /> },
          { path: 'inrichting/eigen-dossiersoorten/nieuw', element: <CustomDossierTypeCreatePage /> },
          { path: 'inrichting/eigen-dossiersoorten/:id/bewerken', element: <CustomDossierTypeEditPage /> },
          { path: 'inrichting/eigen-dossiersoorten/:id/verwijderen', element: <CustomDossierTypeDeletePage /> },
          /* Story be-fe-templates-beheer: de mail-, sms- en WhatsApp-templates van de eigen vestiging.
           * Net als Eigen dossiersoorten BEWUST zonder repairersOnly-guard: zonder vestiging geeft de BE
           * 403 no_repairer_context en toont het scherm "Login als vestiging". */
          { path: 'inrichting/templates', element: <MessageTemplatesListPage /> },
          { path: 'inrichting/templates/nieuw', element: <MessageTemplateCreatePage /> },
          { path: 'inrichting/templates/:id', element: <MessageTemplateEditPage /> },
          /* Story fe-be-bestellijst-overzicht (S-INK.1, 2026-06-18): de
           * Bestellijst onder de nieuwe app "Inkoop" (read-only basislijst). */
          { path: 'inkoop/bestellijst', element: <BestellijstPage /> },
          /* Story fe-be-inkoop-leveranciers-beheer (2026-06-19): leverancier-beheer
           * CRUD onder de Inkoop-app. GÉÉN rol-gate — spiegelt Prijslijsten/
           * Artikeltypes (gewone authenticated routes): open voor elke ingelogde
           * user, veiligheid via BE company-scoping (SuppliersController =
           * [SessionAuth] + company-scope, geen rol-check). Zo beheren herstellers
           * hun eigen leveranciers. */
          { path: 'inkoop/leveranciers', element: <LeveranciersListPage /> },
          { path: 'inkoop/leveranciers/nieuw', element: <LeveranciersCreatePage /> },
          { path: 'inkoop/leveranciers/:id/bewerken', element: <LeveranciersEditPage /> },
          { path: 'inkoop/leveranciers/:id/verwijderen', element: <LeveranciersDeletePage /> },
          /* EPIC-022 S022.9 (be-fe-interne-leverancier): Leveranciersvoorraad van een interne leverancier en het inlezen
           * ervan. Geen rol-gate: de poort van api/internal-suppliers beslist (403/404/503 op het scherm). */
          { path: 'inkoop/leveranciers/intern/:poolId', element: <InternalSupplierStockPage /> },
          { path: 'inkoop/leveranciers/intern/:poolId/inlezen', element: <InternalSupplierImportPage /> },
          /* Story fe-be-inkoop-orders-zoeken-inzien (S-INK.3, 2026-06-21): orders
           * zoeken (lijst) + read-only order-detail onder de Inkoop-app. GÉÉN
           * rol-gate — spiegelt Bestellijst/Leveranciers (company-scoping
           * server-side via de stock-API-token). */
          { path: 'inkoop/orders', element: <OrdersPage /> },
          { path: 'inkoop/orders/:orderId', element: <OrderDetailPage /> },
          /* EPIC-022 S022.1 (be-fe-voorraad-fundament): Inkoop → Voorraad, alleen bij whoami inventoryInCarculate (anders
           * terug naar de bestellijst). /inkoop/voorraad verwijst in S022.1 door naar Magazijnen en locaties; S022.2 zet
           * daar het overzicht. De autoriteit blijft de poort van api/inventory/warehouses (403/503 op het scherm). */
          {
            element: <InventoryRouteGuard />,
            children: [
              { path: 'inkoop/voorraad', element: <Navigate to="/inkoop/voorraad/magazijnen" replace /> },
              { path: 'inkoop/voorraad/magazijnen', element: <WarehousesPage /> },
              /* EPIC-022 S022.3 (be-fe-voorraad-inlezen): een telling uit Excel/CSV inlezen; de knop staat op Magazijnen
               * en locaties tot S022.2 het overzicht bouwt (zelfde route). */
              { path: 'inkoop/voorraad/inlezen', element: <CountImportPage /> },
              { path: 'inkoop/voorraad/*', element: <Navigate to="/inkoop/voorraad/magazijnen" replace /> },
            ],
          },
          /* Story fe-be-facturatie-export-achtergrond (2026-06-26): de
           * Facturatie-app — Export-tab. GÉÉN rol-gate — spiegelt Leveranciers/
           * Bestellijst (gewone authenticated route): open voor elke ingelogde
           * hersteller-user, veiligheid via BE company-scoping
           * (FacturatieExportController = [SessionAuth] + company-scope). De
           * "Facturen"-tab blijft disabled ("Binnenkort") → geen route. */
          /* Story fe-be-facturatie-dashboard (2026-07-15): het facturatie-
           * dashboard (omzet per maand jaar-over-jaar + KPI-strip). OPEN route
           * (GÉÉN <ProtectedRoute repairersOnly>) — het is de default landing van
           * de Facturatie-app; een hersteller-only-gate zou een niet-hersteller op
           * een onbereikbare defaultRoute laten landen. Veiligheid via BE owner-
           * only-scope (FacturatieDashboardController = [SessionAuth] +
           * CurrentSession.CompanyId). */
          { path: 'facturatie/dashboard', element: <FacturatieDashboardPage /> },
          { path: 'facturatie/export', element: <FacturatieExportPage /> },
          /* Story fe-be-facturen-overzicht-journaalregels (2026-07-13): het
           * Facturen-overzicht (zoekscherm) + factuur-detail (factuurregels +
           * journaalregels-tab). GÉÉN rol-gate — open voor elke ingelogde
           * hersteller-user; veiligheid via BE company-scoping (InvoicesController
           * = [SessionAuth] + OwnerID-scope / CanRead → 404-no-leak). Statische
           * route vóór de param-route (leesbaarheid). */
          { path: 'facturatie/facturen', element: <FacturenOverzichtPage /> },
          { path: 'facturatie/facturen/:id', element: <FactuurDetailPage /> },
          /* Story be-fe-facturatie-run-controle (R1, PB-R1-8): Runs — controle vooraf van een facturatie-run.
           * BEWUST niet onder <ProtectedRoute repairersOnly>: de eigenaar van de abonnementen hoeft geen
           * hersteller te zijn. De pagina leest zelf `options` en toont bij 403 "Geen toegang" en bij 503
           * "Kon niet bepalen of je runs mag bekijken" — nooit een lege pagina. */
          /* 10-10-2026: verhuisd naar de eigen app "Interne facturatie". De oude URL's sturen door (met het
           * run-id en de query, bv. `?owner=`), zodat bookmarks en meldingen blijven werken. */
          { path: 'interne-facturatie/runs', element: <BillingRunsPage /> },
          /* Wens eigenaar 10-10-2026: "Nieuwe run" op een eigen scherm. Statisch, VÓÓR runs/:runId (zoals
           * herstellers/nieuw); de BE-route van het detail heeft bovendien `{runId:guid}`, dus "nieuw" is nooit een run. */
          { path: 'interne-facturatie/runs/nieuw', element: <BillingRunNewPage /> },
          { path: 'interne-facturatie/runs/:runId', element: <BillingRunDetailPage /> },
          { path: 'facturatie/runs/*', element: <PrefixRedirect from="/facturatie/runs" to="/interne-facturatie/runs" /> },
          /* Story grootboeken-inrichten (fase 1) + grootboeken-hersteller-only:
           * grootboeken per debiteurengroep inrichten (geen inkoop). Hersteller-
           * ONLY: de bootstrap-GET zaait debiteurengroepen + grootboeken, dus
           * System/RegularUser mogen dit scherm NIET openen (anders kregen zij ten
           * onrechte alle grootboeken op hun eigen company). Route hard gegate via
           * <ProtectedRoute repairersOnly> (mirror BE-gate
           * LedgersController.IsRepairerContext → 403 no_repairer_context). De
           * overige facturatie-routes blijven open (BE company-scoped). */
          {
            element: <ProtectedRoute repairersOnly />,
            children: [
              { path: 'facturatie/grootboeken', element: <GrootboekenPage /> },
              /* Story fe-be-factuurdocumenten-story1 (2026-07-14): het READ-ONLY
               * beheerscherm van de UF_InvoiceReport-matrix (factuurtype × verzekerd
               * × credit). Hersteller-ONLY (PO-keuze, gespiegeld op Grootboeken):
               * zelfde <ProtectedRoute repairersOnly>-gate → System/RegularUser die
               * de URL intypt wordt geredirect. BE blijft company-gescoped. */
              { path: 'facturatie/factuurdocumenten', element: <InvoiceDocumentsPage /> },
              /* Story exportinstellingen (2026-07-15): het export-blok van
               * legacy Glas/InvoiceSettings (verkoopdagboek, exportroutine, ER/
               * btw-keuzes, omzet-grootboek per btw-tarief). Hersteller-ONLY: de
               * bootstrap-GET zaait UF_CompanyInvoiceSetting-keys, dus System/
               * RegularUser mogen dit scherm NIET openen (mirror BE-gate
               * ExportSettingsController.IsRepairerContext → 403
               * no_repairer_context). Zelfde <ProtectedRoute repairersOnly>-gate. */
              { path: 'facturatie/exportinstellingen', element: <ExportinstellingenPage /> },
              /* Story nummering-factuurreeks-per-jaar (2026-07-15): de Nummering-tab
               * van legacy Glas/InvoiceSettings (start-/volgnummer per reeks) + de
               * opt-in "factuurnummer per jaar laten oplopen". Hersteller-ONLY: de
               * bootstrap-GET zaait UF_CompanyInvoiceSetting-keys, dus System/
               * RegularUser mogen dit scherm NIET openen (mirror BE-gate
               * NumberingController.IsRepairerContext → 403 no_repairer_context).
               * Zelfde <ProtectedRoute repairersOnly>-gate. */
              { path: 'facturatie/nummering', element: <NummeringPage /> },
            ],
          },
          /* Story fe-be-documentatie-module (2026-07-09): de data-gedreven
           * Documentatie-app. GÉÉN rol-/system-gate op de route — open voor
           * ELKE ingelogde user (read-only voor gewone gebruikers; beheerbaar
           * voor system/keten). De lees-/schrijf-gate wordt BE-side afgedwongen
           * (afgeschermde items ontbreken in de boom, 403/404 bij directe hit).
           * De detail-route `/documentatie/:documentId` geeft document-navigatie
           * een echte `href` → rechtsklik/ctrl/middel-klik-nieuw-tab werkt. */
          { path: 'documentatie', element: <DocumentationPage /> },
          { path: 'documentatie/:documentId', element: <DocumentationPage /> },
          /* Story rapportage-per-rol-fase1 (2026-07-11): de Rapportage-app
           * (Scherm B). GÉÉN rol-/system-gate — elke ingelogde user; de BE
           * bepaalt via precedence welke rapportenboom (rol zonder menu → lege
           * staat). De rapport-detail-route is de FASE-1-placeholder: een
           * rapport-kaart is een echte `<Link href>` (rechtsklik-nieuw-tab) en
           * een gewone klik landt hier op de nette "komt in fase 2"-pagina. */
          { path: 'rapportage', element: <RapportageAppPage /> },
          { path: 'rapportage/rapport/:id', element: <RapportParamsPage /> },
          /* Story fe-be-nieuwsberichten (2026-07-11): het Nieuwsberichten-menu
           * (lijst + detail) — bereikbaar vanuit Glas én Documentatie. GÉÉN
           * rol-/system-gate: iedereen ziet z'n eigen gescoopte feed. De beheer-
           * subroutes (system ‖ keten) worden FE-side afgeschermd via canManage én
           * BE-side her-gate't (403). Detail-route geeft rij-navigatie een echte
           * `href` (rechtsklik-nieuw-tab). Beheer-routes vóór `:id` zodat de
           * statische segmenten winnen. */
          { path: 'nieuwsberichten/beheer', element: <NewsAdminListPage /> },
          { path: 'nieuwsberichten/beheer/nieuw', element: <NewsEditPage /> },
          { path: 'nieuwsberichten/beheer/:id/bewerken', element: <NewsEditPage /> },
          { path: 'nieuwsberichten', element: <NewsMenuPage /> },
          { path: 'nieuwsberichten/:id', element: <NewsMenuPage /> },
          /* Story fe-be-controleredenen-beheer-crud-scaffold (story 3, 2026-06-05).
           * Rol-gate: `glasadmin` OF `hoofdkantoor` (multi-rol, 2026-06-06) —
           * geneste ProtectedRoute met roles (gebruikt `.some()`). Functioneel
           * dankzij A14 (auth vult roles uit WF_Role). */
          {
            element: <ProtectedRoute roles={['glasadmin', 'hoofdkantoor']} />,
            children: [
              { path: 'inrichting/controleredenen', element: <ControleredenenListPage /> },
              { path: 'inrichting/controleredenen/nieuw', element: <ControleredenenCreatePage /> },
              { path: 'inrichting/controleredenen/:id/bewerken', element: <ControleredenenEditPage /> },
              { path: 'inrichting/controleredenen/:id/verwijderen', element: <ControleredenenDeletePage /> },
            ],
          },
          /* Story fe-be-dossierdocumenten-story1: verzendregels-beheerscherm.
           * GÉÉN rol-gate — company-scoped inrichting (BE dwingt de sessie-company
           * af); spiegelt Monteurs/Vervangend vervoer. */
          { path: 'inrichting/verzendregels', element: <VerzendregelsPage /> },
          /* Story fe-be-claimfilter-beheerscherm (2026-08-31): het
           * claimfilter-beheerscherm — vier subschermen onder `/claimfilter/*`.
           *
           * WAARSCHUWING: `systemOrAnyRole` en NADRUKKELIJK NIET `systemOnly`
           *    samen met `roles`. Die twee componeren met **EN**; samen gezet zou
           *    de route voor NIEMAND bereikbaar zijn — zonder foutmelding, en dus
           *    van buiten niet te onderscheiden van "nog niet gebouwd" (AC-5.3).
           *    De poort leest dezelfde gedeelde definitie als de app-tegel en de
           *    tab-strip, met dezelfde twee termen als de BE-poort (AC-40). */
          {
            element: <ProtectedRoute systemOrAnyRole={CLAIMFILTER_AUDIENCE_ROLES} />,
            children: [
              { path: 'claimfilter', element: <ClaimFilterOverviewPage /> },
              { path: 'claimfilter/rondes', element: <ClaimFilterRoundsPage /> },
              { path: 'claimfilter/dossiers', element: <ClaimFilterDossiersPage /> },
              /* Het vijfde subscherm (§5.7): de lijst en het detail. ⚠ Het
               * detail keyt op `Number` en niet op de GUID — dat is wat een
               * mens in de lijst ziet en wat leesbaar blijft in de URL. Beide
               * erven dezelfde poort als de andere vier (AC-57.4). */
              { path: 'claimfilter/claimfilters', element: <ClaimFilterRecordsPage /> },
              { path: 'claimfilter/claimfilters/:number', element: <ClaimFilterRecordDetailPage /> },
              /* ⚠⚠ DE OORDEELSREGELS STAAN HIER BEWUST NIET (MEER) — zie de
               *    top-level route verderop. Ze stonden hier tot 09-09-2026, toen de
               *    verzekeraar er nog buiten werd gehouden; nu zij erbij komt zou deze
               *    groep haar buitensluiten met een 404 die niemand kan plaatsen,
               *    omdat het endpoint haar wél kent. */
              { path: 'claimfilter/gezondheid', element: <ClaimFilterHealthPage /> },
            ],
          },
          /* Story be-fe-systeem-gebruikers (24-10-2026): Systeem → Gebruikers. BEWUST niet onder systemOnly: het
           * keten-hoofdkantoor hoort erbij. De gate is whoami `canManageUsers`; elk api/system/users-endpoint toetst zelf
           * opnieuw (403/404/503). */
          {
            element: <ProtectedRoute canManageUsers />,
            children: [
              { path: 'system/users', element: <SystemUsersPage /> },
              { path: 'system/users/:id', element: <SystemUserDetailPage /> },
            ],
          },
          /* Verhuizing 2026-06-26: mailbox-beheer (CRUD-scaffold, story
           * fe-be-polis-reply-mailbox-poliscontrole) verhuist van Inrichting
           * naar de eigen app "System" onder het prefix `/system/mailboxen/*`.
           * System-only gate via <ProtectedRoute systemOnly> — niet-system-users
           * krijgen een redirect naar `/`. De app-tile + tab zijn parallel
           * gegate via App.requiresSystemContext in apps.config. */
          {
            element: <ProtectedRoute systemOnly />,
            children: [
              { path: 'system/mailboxen', element: <MailboxenListPage /> },
              { path: 'system/mailboxen/nieuw', element: <MailboxenCreatePage /> },
              { path: 'system/mailboxen/:id/bewerken', element: <MailboxenEditPage /> },
              { path: 'system/mailboxen/:id/verwijderen', element: <MailboxenDeletePage /> },
              /* Story fe-systeemberichten (2026-06-26): beheer van de globale
               * systeemberichten-banner. Zelfde system-only gate als mailboxen. */
              { path: 'system/berichten', element: <SystemMessagesListPage /> },
              { path: 'system/berichten/nieuw', element: <SystemMessagesCreatePage /> },
              { path: 'system/berichten/:id/bewerken', element: <SystemMessagesEditPage /> },
              { path: 'system/berichten/:id/verwijderen', element: <SystemMessagesDeletePage /> },
              /* Story rapportage-per-rol-fase1 (2026-07-11): beheerscherm A
               * "Rapportage-menu per rol" — verplaatst van Inrichting naar de
               * System-app (2026-07-13). System-only via deze <ProtectedRoute
               * systemOnly>-groep; de tab hangt onder de System-app. */
              { path: 'system/rapportage-menu', element: <RapportageMenuPerRolePage /> },
              /* Story be-fe-support-soorten-foutmelding-monteur (S3, AC 13): Support → Soorten. System-only via deze
               * groep (het keten-HK behandelt wel, maar richt niet in); elk api/support/types-endpoint toetst zelf. */
              { path: 'support/types', element: <SupportTypesPage /> },
              /* Story be-fe-nh1816-partijstructuur-en-aanlevering (2026-09-17):
               * de aanleverende ketens en vestigingen van de MAILSTROOM, met hun
               * herkenningskenmerken en hun externe code per verzekeraar.
               * System-only via deze <ProtectedRoute systemOnly>-groep; het
               * endpoint doet de toets zelf opnieuw (403) — de route-gate is voor
               * de gebruiker, de server-gate is de poort.
               * ⚠ Er is bewust GEEN verwijder-route voor een keten of een
               *   vestiging (AC-19): daar hangen aanleveringen aan. */
              { path: 'system/declaration-parties', element: <DeclarationChainsPage /> },
              { path: 'system/declaration-parties/chains/:chainId', element: <DeclarationChainDetailPage /> },
              {
                path: 'system/declaration-parties/branches/:branchId',
                element: <DeclarationBranchDetailPage />,
              },
              /* Story be-fe-nh1816-mail-intake-declaraties (2026-09-17): het
               * READ-ONLY zicht op wat er via de declaratie-postvakken binnenkomt
               * (K5/AC-9 + AC-9a). System-only via deze <ProtectedRoute
               * systemOnly>-groep; het endpoint doet de toets zelf opnieuw (403).
               * ⚠ Zonder dit scherm stapelen zich rijen op die niemand kan zien,
               *   terwijl de mail wél uit het postvak is weggehaald — de faalvorm
               *   waarbij je pas na een week merkt dat er niets klopt.
               * ⚠ Geen bewerk-, verwijder- of opnieuw-proberen-route: dat is de
               *   werkvoorraad en die valt buiten scope. */
              {
                path: 'system/declaratie-aanleveringen',
                element: <DeclarationSubmissionsPage />,
              },
              /* Story be-fe-configuratie-overzicht (2026-09-30): welke instellingen
               * kent deze omgeving, wat is gevuld, en wat moet ik bij een deploy
               * nalopen. READ-ONLY. System-only via deze <ProtectedRoute systemOnly>
               * én het endpoint doet de toets zelf opnieuw (403).
               * ⚠ Het BE stuurt geen enkele geheime waarde mee — zie
               *   Diagnostics\ConfigurationOverviewKernels.BuildRow. */
              { path: 'system/configuratie', element: <ConfigurationPage /> },
              /* Story be-fe-menu-aan-uit: elke app en elk menu-item aan of uit, voor iedereen of voor één rol.
               * System-only via deze <ProtectedRoute systemOnly> én elk endpoint doet de toets zelf opnieuw (403). */
              { path: 'system/menu', element: <MenuSettingsPage /> },
              /* Story be-fe-partner-api-dossier-aanmaken (S020.1, EPIC-020): de partners van de partner-API —
               * aanmaken, aan/uit, API-sleutel (één keer getoond) en vastgelopen aanvragen vrijgeven.
               * System-only via deze <ProtectedRoute systemOnly> én elk endpoint toetst zelf opnieuw (403). */
              { path: 'system/partners', element: <PartnersPage /> },
              /* Story be-fe-systeem-logboek: de logregels van AppServices zoeken en lezen. System-only via deze
               * <ProtectedRoute systemOnly> én elk endpoint (lock-vrij) toetst zelf opnieuw (403). */
              { path: 'system/log', element: <TraceLogPage /> },
              { path: 'system/log/:id', element: <TraceLogDetailPage /> },
              /* Story be-fe-standaarddocumenten (G2, PB-S1): de standaardlijst voor de hele instantie.
               * De tab staat onder Inrichting (requiresSystemContext op tab-niveau); de route is hier
               * hard gegate en het endpoint toetst zelf opnieuw (403 role_not_allowed). */
              { path: 'inrichting/standaarddocumenten', element: <DefaultDocumentsPage /> },
            ],
          },
            ],
          }, /* einde RestrictedRepairerRouteGuard-children (S-C, C1) */
            ],
          }, /* einde InsurerRouteGuard-children */
        ],
      },
          ],
        }, /* einde ContactVerificationRouteGuard-children (be-fe-contactgegevens-bevestigen) */
        ],
      }, /* einde FitterOnlyRouteGuard-children (S021.1) */
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];
